/*
 * Forced-payment race signup helpers.
 *
 * An unpaid reservation (paymentRequiredBeforeApproval, not approved, required
 * payment missing) holds a slot for a limited time (20 minutes unless the race
 * sets signupPaymentReservationMinutes) but is not a public signup yet:
 * public result/signup lists must not show it, and payment CTAs must disappear
 * the moment the countdown reaches zero (the backend deletes the record via a
 * TTL index shortly after, so the UI must not wait for that).
 */

// Minutes a forced-payment signup holds its seat. Mirrors
// backend/lib/race-class-signup.js getPaymentReservationMinutes: unset, zero or
// invalid falls back to 20, otherwise clamped to 5..1440.
export const DEFAULT_PAYMENT_RESERVATION_MINUTES = 20;
const MIN_PAYMENT_RESERVATION_MINUTES = 5;
const MAX_PAYMENT_RESERVATION_MINUTES = 1440;

export function getPaymentReservationMinutes(race = {}) {
    const configured = Math.floor(Number(race?.signupPaymentReservationMinutes));
    if (!(configured > 0)) {
        return DEFAULT_PAYMENT_RESERVATION_MINUTES;
    }
    return Math.min(MAX_PAYMENT_RESERVATION_MINUTES, Math.max(MIN_PAYMENT_RESERVATION_MINUTES, configured));
}

// Returns a NEW array without unpaid forced-payment reservations. Never
// mutates the input (store arrays are shared).
export function withoutUnpaidReservations(contestants) {
    if (!Array.isArray(contestants)) {
        return [];
    }
    return contestants.filter(contestant => contestant && !contestant.paymentRequiredBeforeApproval);
}

// Seconds left of the payment reservation, corrected for client/server clock
// skew via paymentReservationServerNow (stamped by the GET route) and
// paymentReservationClientLoadedAt (stamped by the store on load).
export function getReservationSeconds(contestant = {}, now = Date.now()) {
    if (!contestant.paymentReservationExpiresAt) {
        return 0;
    }
    const expiresAt = new Date(contestant.paymentReservationExpiresAt).getTime();
    const serverNow = new Date(contestant.paymentReservationServerNow).getTime();
    const clientLoadedAt = Number(contestant.paymentReservationClientLoadedAt);
    if (!Number.isNaN(serverNow) && clientLoadedAt > 0) {
        const remainingAtLoad = expiresAt - serverNow;
        return Math.max(0, Math.ceil((remainingAtLoad - (now - clientLoadedAt)) / 1000));
    }
    return Math.max(0, Math.ceil((expiresAt - now) / 1000));
}

// Admin "add contestant" search: when several accounts share the same name
// (duplicates), the FIRST one in the list gets `recommended: true` - the list
// arrives pre-ranked by race history (signupCount + resultCount) from the
// backend, so the first duplicate is the account to propose. Returns a NEW
// array with new objects; unique names are left unmarked.
export function markRecommendedDuplicates(users) {
    if (!Array.isArray(users)) {
        return [];
    }
    const cleaned = users.filter(user => user).map(user => ({ ...user }));
    const nameCounts = {};
    const nameKey = user => `${user.firstname || ''} ${user.lastname || ''}`.trim().toLowerCase()
        .replace(/\s+/g, ' ');
    cleaned.forEach(user => {
        const key = nameKey(user);
        if (key) {
            nameCounts[key] = (nameCounts[key] || 0) + 1;
        }
    });
    const marked = new Set();
    cleaned.forEach(user => {
        const key = nameKey(user);
        if (key && nameCounts[key] > 1 && !marked.has(key)) {
            marked.add(key);
            user.recommended = true;
        }
    });
    return cleaned;
}

// True while the signup still needs its required online payment.
export function isReservationPending(contestant = {}) {
    if (!contestant || !contestant.paymentRequiredBeforeApproval || contestant.approved) {
        return false;
    }
    const paidField = contestant.requiredPaymentType === 'signupfee' ? 'paidSignupfee' : 'paidSignup';
    return !contestant[paidField];
}
